import { buildApiUrl } from "../../config/api";
import { withCredentials } from "../../apiClient";
import { createLogger } from "../../utils/logger";

const log = createLogger("AiClientLogEndpoint");

interface OpenAiClientLogCommonDetails {
  requestLabel: string;
  attempt: number;
  maxAttempts: number;
  requestBytes: number;
  durationMs: number;
  signalAborted: boolean;
}

interface OpenAiSyntheticHtml400Details extends OpenAiClientLogCommonDetails {
  status: 400;
  blockedBy: "cloudflare" | "google_frontend" | "proxy" | "unknown";
  cfRay?: string;
  bodyHash: string;
  htmlContentType: boolean;
  cfMitigated: boolean;
  viaPresent: boolean;
  responseBytes: number;
  willRetry: boolean;
}

interface OpenAiNoResponseDetails extends OpenAiClientLogCommonDetails {
  responseStage: "headers" | "body";
  failureKind:
    | "aborted"
    | "timeout"
    | "load_failed"
    | "failed_to_fetch"
    | "network_error"
    | "other";
}

interface OpenAiTransientStatusRetryDetails extends OpenAiClientLogCommonDetails {
  status: number;
  /** The wait before the retry: the server's retry-after, or the backoff. */
  retryAfterMs: number;
}

interface StreamFailureRetryDetails extends OpenAiClientLogCommonDetails {
  /** The backoff before replaying the request. */
  retryAfterMs: number;
}

interface AiClientLogDetailsByEvent {
  openai_responses_synthetic_html_400: OpenAiSyntheticHtml400Details;
  openai_responses_no_response: OpenAiNoResponseDetails;
  openai_responses_transient_status_retry: OpenAiTransientStatusRetryDetails;
  openai_responses_stream_failure_retry: StreamFailureRetryDetails;
  anthropic_messages_synthetic_html_400: OpenAiSyntheticHtml400Details;
  anthropic_messages_no_response: OpenAiNoResponseDetails;
  anthropic_messages_transient_status_retry: OpenAiTransientStatusRetryDetails;
  anthropic_messages_stream_failure_retry: StreamFailureRetryDetails;
}

export type AiClientLogEvent = keyof AiClientLogDetailsByEvent;

/** Reports local delivery failure without including the rejected telemetry payload. */
function warnUnableToWrite(event: AiClientLogEvent): void {
  log.warn("Unable to write AI client log", { event });
}

/**
 * Sends a typed, privacy-bounded diagnostic event without delaying the model
 * request. Delivery is best effort: callers continue even when the telemetry
 * endpoint is unavailable or rejects the payload.
 */
export function logAiClientEvent<Event extends AiClientLogEvent>(
  event: Event,
  details: AiClientLogDetailsByEvent[Event]
): void {
  try {
    void fetch(
      buildApiUrl("/api/ai/client-log"),
      withCredentials({
        method: "POST",
        body: JSON.stringify({ event, details }),
        keepalive: true,
      })
    )
      .then(async (response) => {
        if (response.ok) return;
        await response.body?.cancel().catch(() => undefined);
        warnUnableToWrite(event);
      })
      .catch(() => warnUnableToWrite(event));
  } catch {
    warnUnableToWrite(event);
  }
}
